import { Button, Field, Input, makeStyles, Textarea } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import IEnquiry, { NewEnquiry } from "../../models/contact/IEnquiry";
import contactService from "../../services/ContactService";
import { useState } from "react";
import Status from "../../enums/Status";
import { EnquiryFailedMessage, EnquirySubmittedMessage } from "./Messages";

const useStyles = makeStyles({
    formField: {
        marginBottom: '1rem'
    },
    formInput: {
        border: '1px solid #E5E7EB',
        borderRadius: '6px',
        transition: 'all 0.2s ease'
    },
    contactButton: {
        width: '100%',
        borderRadius: '4px',
        textTransform: 'uppercase',
    }
});

export default function Form() {
    const { t } = useTranslation("contact");
    const styles = useStyles();

    const [status, setStatus] = useState<Status>(Status.NEURAL);
    const [enquiry, setEnquiry] = useState<IEnquiry>(NewEnquiry());

    const handleFieldChange = (field: string, value: string) => setEnquiry((e) => ({ ...e, [field]: value }));

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        await submitEnquiry(enquiry, setStatus);
    };

    const isDisabled = [Status.SAVING, Status.SAVE_SUCCESS].includes(status);

    return (
        <form onSubmit={handleSubmit}>
            
            <Field className={styles.formField}>
                <Input
                    type='text'
                    size='large'
                    value={enquiry.name}
                    disabled={isDisabled}
                    className={styles.formInput}
                    placeholder={t("form.fields.name.placeholder")}
                    onChange={(e) => handleFieldChange("name", e.target.value)}
                    />
            </Field>

            <Field className={styles.formField}>
                <Input
                    type='tel'
                    size='large'
                    value={enquiry.phone}
                    disabled={isDisabled}
                    className={styles.formInput}
                    placeholder={t("form.fields.phone.placeholder")}
                    onChange={(e) => handleFieldChange("phone", e.target.value)}
                    />
            </Field>

            <Field className={styles.formField}>
                <Input
                    type='email'
                    size='large'
                    value={enquiry.email}
                    disabled={isDisabled}
                    className={styles.formInput}
                    placeholder={t("form.fields.email.placeholder")}
                    onChange={(e) => handleFieldChange("email", e.target.value)}
                    />
            </Field>

            <Field className={styles.formField}>
                <Textarea
                    rows={4}
                    size='large'
                    disabled={isDisabled}
                    value={enquiry.message}
                    className={styles.formInput}
                    placeholder={t("form.fields.message.placeholder")}
                    onChange={(e) => handleFieldChange("message", e.target.value)}
                    />
            </Field>

            {
                status === Status.SAVE_ERROR && <EnquiryFailedMessage /> 
            }

            {
                status === Status.SAVE_SUCCESS && <EnquirySubmittedMessage /> 
            }

            {
                [Status.NEURAL, Status.SAVING, Status.SAVE_ERROR].includes(status) &&
                <Button type="submit" appearance="primary" size="large" disabled={isDisabled} className={styles.contactButton}>
                    {t("form.button")}
                </Button>
            }

        </form>
    );
}

async function submitEnquiry (
    enquiry: IEnquiry,
    setStatus: (status: Status) => void
) {
    setStatus(Status.SAVING);

    try {
        const result = await contactService.submitEnquiry(enquiry);
        setStatus(result ? Status.SAVE_SUCCESS : Status.SAVE_ERROR);
    } catch (e) {
        setStatus(Status.SAVE_ERROR);
    }
}